<template>
  <div>
    <h1>Votre identification professionnelle</h1>
    <form @submit.prevent="handleSubmit">
      <FormInput
        id="register-rpps"
        v-model="form.rpps"
        :disabled="form.noIdentification"
        label="Numéro RPPS"
        maxlength="11"
        required
        @blur="$v.form.rpps.$touch()"
      >
        <template v-if="$v.form.rpps.$dirty || $v.form.adeli.$dirty" #error>
          <template v-if="!$v.form.rpps.required || !$v.form.adeli.required">Veuillez renseigner un des deux champs ou
            cocher la case
          </template>
          <template v-else-if="!$v.form.rpps.integer">Le numéro RPPS est composé de chiffres uniquement</template>
          <template v-else-if="$v.form.rpps.$invalid">Le numéro RPPS est invalide</template>
          <template v-else-if="rppsAlreadyUsed">Ce numéro RPPS est déjà utilisé</template>
        </template>
      </FormInput>
      <FormInput
        id="register-adeli"
        v-model="form.adeli"
        :disabled="form.noIdentification"
        label="Numéro ADELI / AM / FINESS"
        maxlength="9"
        required
        @blur="$v.form.adeli.$touch()"
      >
        <template v-if="$v.form.rpps.$dirty || $v.form.adeli.$dirty" #error>
          <template v-if="!$v.form.rpps.required || !$v.form.adeli.required">Veuillez renseigner un des deux champs ou
            cocher la case
          </template>
          <template v-else-if="$v.form.adeli.$invalid">Le numéro ADELI / AM / FINESS est invalide</template>
          <template v-else-if="adeliAlreadyUsed">Ce numéro ADELI est déjà utilisé</template>
        </template>
      </FormInput>

      <FormCheckbox v-model="form.noIdentification">
        Je n'ai pas de numéro Adeli ou RPPS mais certifie être titulaire d’un diplôme autorisant la pratique de ma
        spécialité que je présenterai à Hellocare sans délai.
      </FormCheckbox>

      <DashedLine />

      <p>Si vous faites partie d'une organisation territoriale</p>

      <FormSelect id="to-type" v-model="form.territorialOrganizationType" label="Sélectionnez le type d'organisation">
        <option :value="null">Je ne fais pas partie d'une organisation territoriale</option>
        <option value="cpts">CPTS</option>
        <option value="esp">ESP</option>
        <option value="msp">MSP</option>
        <option value="cds">CDS</option>
        <option value="other">Autre</option>
        <template
          v-if="$v.form.territorialOrganizationType.$dirty && !$v.form.territorialOrganizationType.valid"
          #error
        >
          Veuillez sélectionner un type d'organisation
        </template>
        <template #help>
          Si vous faites partie de plusieurs organisations, merci de choisir celle avec la territorialité la plus
          étendue
        </template>
      </FormSelect>

      <FormInput
        v-if="form.territorialOrganizationType !== null"
        id="to-name"
        v-model="form.territorialOrganizationName"
        label="Nom de votre organisation territoriale"
        max-length="128"
      >
        <template v-if="$v.form.territorialOrganizationName.$dirty" #error>
          <template v-if="!$v.form.territorialOrganizationName.length">128 caractères maximum</template>
        </template>
      </FormInput>

      <SignupButtons :can-be-submitted="!$v.$invalid" :is-submitting="isSubmitting" />
    </form>
  </div>
</template>

<script>
  import SignupButtons from '@/components/Signup/SignupButtons';
  import FormInput from '@/components/Form/FormInput';
  import FormCheckbox from '@/components/Form/FormCheckbox';
  import DashedLine from '@/components/DashedLine';
  import FormSelect from '@/components/Form/FormSelect';
  import { alphaNum, integer, maxLength, requiredIf } from '@byscripts/vuelidate/lib/validators';
  import practitioners from '@/api-connect/practitioners';

  export default {
    name: 'SignupIdentification',
    components: { FormSelect, DashedLine, FormCheckbox, FormInput, SignupButtons },
    data() {
      return {
        isSubmitting: false,
        rppsAlreadyUsed: false,
        adeliAlreadyUsed: false,
        form: {
          rpps: this.$store.state.signup.form.rpps,
          adeli: this.$store.state.signup.form.adeli,
          noIdentification: this.$store.state.signup.form.noIdentification,
          territorialOrganizationType: this.$store.state.signup.form.territorialOrganizationType || null,
          territorialOrganizationName: this.$store.state.signup.form.territorialOrganizationName,
        },
      };
    },
    watch: {
      'form.rpps'() {
        this.rppsAlreadyUsed = false;
      },
      'form.adeli'() {
        this.adeliAlreadyUsed = false;
      },
    },
    validations: {
      form: {
        rpps: {
          required: requiredIf(function () { return !this.form.adeli && !this.form.noIdentification; }),
          length: maxLength(11),
          integer,
        },
        adeli: {
          required: requiredIf(function () { return !this.form.rpps && !this.form.noIdentification;}),
          length: maxLength(9),
          alphaNum,
        },
        noIdentification: {
          required: requiredIf(function () { return !this.form.adeli && !this.form.rpps;}),
        },
        territorialOrganizationType: {
          valid: (type) => [null, 'cpts', 'esp', 'msp', 'cds', 'other'].includes(type),
        },
        territorialOrganizationName: {
          length: maxLength(128),
        },
      },
    },
    methods: {
      async handleSubmit() {
        this.$v.$touch();

        if (this.$v.$invalid) {
          return this.$addError('Veuillez vérifier le formulaire');
        }

        try {
          this.isSubmitting = true;

          if (!this.form.noIdentification) {
            await practitioners.checkIdentification(this.form.rpps, this.form.adeli);
          }

          await this.$store.dispatch('signup/saveForm', { form: this.form });
          await this.$store.dispatch('signup/goToNextStep');
        } catch (e) {
          if (!e.response || e.response.status !== 400) {
            await this.$addError('Une erreur est survenue. Veuillez réessayer dans quelques instants.');
            throw e;
          }

          const response = await e.response.json();

          this.rppsAlreadyUsed = response.message.includes('RPPS');
          this.adeliAlreadyUsed = response.message.includes('ADELI');

          return this.$addError('Veuillez vérifier le formulaire');
        } finally {
          this.isSubmitting = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .form-input, .form-select {
    margin-bottom: 3rem;
  }

  .dashed-line {
    margin: 4rem 0 3rem;
  }
  ::v-deep .signup-buttons.has-back-button{
    @include onMobile{
      column-gap: 3.7rem;
      justify-content: end;
      flex-wrap: wrap;
    }
  }
</style>
